import type { ActivityFeedEntry } from '@/domain/types';
import { relativeFromIso, simTimeShort } from '@/utils/format';
import {
  PhoneIncoming,
  PhoneOutgoing,
  MessageSquare,
  Sparkles,
  Wallet,
  Calendar,
  RotateCcw,
  Hash,
  ShieldCheck,
  Radio,
  Armchair,
  NotebookPen,
  ListPlus,
} from 'lucide-react';
import clsx from 'clsx';

const iconForType: Record<ActivityFeedEntry['type'], typeof MessageSquare> = {
  'inbound-call': PhoneIncoming,
  'outbound-call': PhoneOutgoing,
  'call-completed': PhoneOutgoing,
  'meta-dm': MessageSquare,
  'sms-sent': MessageSquare,
  'sms-received': MessageSquare,
  'deposit-link-sent': Wallet,
  'deposit-paid': Wallet,
  'receipt-sent': Wallet,
  'deposit-expired': Wallet,
  'waiver-sent': ShieldCheck,
  'waiver-signed': ShieldCheck,
  'booking-created': Calendar,
  'booking-cancelled': Calendar,
  'booking-checked-in': Radio,
  'booking-seated': Armchair,
  'booking-session-noted': NotebookPen,
  'booking-completed': Calendar,
  'self-book-link-sent': Hash,
  'self-book-link-used': Hash,
  'ai-handoff': Sparkles,
  'ai-handoff-resolved': Sparkles,
  'ai-voice-handoff': Sparkles,
  'pushed-to-queue': ListPlus,
  'cross-studio-route': RotateCcw,
  'future-readiness-captured': Sparkles,
  'future-readiness-fired': Sparkles,
  'reactivation-warm-handoff': RotateCcw,
  'rebook-attributed': Sparkles,
  'rebook-declined': Calendar,
  'rebook-deferred-to-sales': RotateCcw,
  'payout-signed': Wallet,
  'payout-shortfall': Wallet,
  'float-set': Wallet,
  'day-closed': Wallet,
  'day-reopened': Wallet,
  voicemail: PhoneIncoming,
  cancellation: Calendar,
  'override-requested': ShieldCheck,
  'override-resolved': ShieldCheck,
};

export interface ActivityFeedItemProps {
  entry: ActivityFeedEntry;
  onAction?: () => void;
  actionLabel?: string;
}

export default function ActivityFeedItem({ entry, onAction, actionLabel }: ActivityFeedItemProps) {
  const Icon = iconForType[entry.type];
  const highlight = entry.type === 'deposit-paid' || entry.type === 'booking-created';
  return (
    <div
      data-testid={`activity-item-${entry.id}`}
      className={clsx(
        'rounded-md px-3 py-2 border',
        highlight
          ? 'border-gold-bright/40 bg-gold/5'
          : 'border-gold-dim/15 bg-ink-deep/60',
      )}
    >
      <div className="flex items-start gap-3">
        <Icon size={14} className={clsx('mt-0.5 shrink-0', highlight ? 'text-gold-bright' : 'text-cream-soft')} />
        <div className="flex-1 min-w-0">
          <p className="text-xs leading-relaxed text-cream-soft">{entry.body}</p>
          <p className="font-inter text-[10px] text-gold-dim uppercase mt-1 tracking-widest">
            {simTimeShort(entry.timestamp)} · {relativeFromIso(entry.timestamp)}
          </p>
        </div>
        {actionLabel && onAction ? (
          <button onClick={onAction} className="btn-quiet text-[10px]">
            {actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
